import { trpc } from '@/trpcClient';
import { logger } from '@/utils/logger';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent } from '@shelflix/ui-web/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shelflix/ui-web/components/ui/table';
import { TablePagination } from '@shelflix/ui-web/components/TablePagination';
import Icon from '@shelflix/ui-web/components/Icon';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';

interface TransferLogsTableProps {
  materialUuid: string;
}

export function TransferLogsTable({ materialUuid }: TransferLogsTableProps) {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [previousFacilityUuid, setPreviousFacilityUuid] = useState<string | undefined>();
  const [newFacilityUuid, setNewFacilityUuid] = useState<string | undefined>();
  const [userUuid, setUserUuid] = useState<string | undefined>();
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, isError } = useQuery({
    ...trpc.material.listTransferLogs.queryOptions({
      materialUuid,
      page,
      pageSize,
      dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
      dateTo: dateTo ? new Date(dateTo).toISOString() : undefined,
      previousFacilityUuid,
      newFacilityUuid,
      userUuid,
    }),
  });

  // Fetch facilities for filters
  const { data: facilitiesData } = useQuery(trpc.facility.list.queryOptions({}));

  // Fetch users for filters
  const { data: usersData } = useQuery(trpc.user.list.queryOptions({ page: 1, recordsPerPage: 1000 }));

  const hasActiveFilters = dateFrom || dateTo || previousFacilityUuid || newFacilityUuid || userUuid;

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setPreviousFacilityUuid(undefined);
    setNewFacilityUuid(undefined);
    setUserUuid(undefined);
    setPage(1);
  };

  const formatDateTime = (dateString: string) => {
    try {
      // Parse the string as UTC if it doesn't have timezone info, then format in local timezone
      const utcDateString = dateString.includes('Z') || dateString.includes('+') ? dateString : dateString + 'Z';
      const date = new Date(utcDateString);
      return format(date, "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR });
    } catch (error) {
      logger.error('Error formatting date:', error);
      return dateString;
    }
  };

  if (isError) {
    return (
      <Card className="p-6">
        <p className="text-destructive text-sm">Erro ao carregar histórico de transferências</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Filter Toggle */}
      <div className="flex items-center justify-between px-4">
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-2">
          <Icon name="filter_alt" size={16} />
          <span>Filtros</span>
        </Button>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-2 text-xs">
            <Icon name="close" size={14} />
            <span>Limpar filtros</span>
          </Button>
        )}
      </div>

      {/* Filters Card */}
      {showFilters && (
        <Card className="rounded-[10px] border-none px-8 shadow-none">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex min-w-[200px] flex-1 flex-col gap-2">
                <label htmlFor="dateFrom" className="text-xs font-medium text-[var(--gray-primary)]">
                  Data início
                </label>
                <input
                  id="dateFrom"
                  type="datetime-local"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                  className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div className="flex min-w-[200px] flex-1 flex-col gap-2">
                <label htmlFor="dateTo" className="text-xs font-medium text-[var(--gray-primary)]">
                  Data fim
                </label>
                <input
                  id="dateTo"
                  type="datetime-local"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                  className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div className="flex min-w-[200px] flex-1 flex-col gap-2">
                <label htmlFor="previousFacility" className="text-xs font-medium text-[var(--gray-primary)]">
                  Estabelecimento anterior
                </label>
                <Select
                  value={previousFacilityUuid || 'all'}
                  onValueChange={(value) => {
                    setPreviousFacilityUuid(value === 'all' ? undefined : value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="previousFacility" className="h-10 w-full">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {facilitiesData?.facilities?.map((facility) => (
                      <SelectItem key={facility.uuid} value={facility.uuid}>
                        {facility.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex min-w-[200px] flex-1 flex-col gap-2">
                <label htmlFor="newFacility" className="text-xs font-medium text-[var(--gray-primary)]">
                  Novo estabelecimento
                </label>
                <Select
                  value={newFacilityUuid || 'all'}
                  onValueChange={(value) => {
                    setNewFacilityUuid(value === 'all' ? undefined : value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="newFacility" className="h-10 w-full">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {facilitiesData?.facilities?.map((facility) => (
                      <SelectItem key={facility.uuid} value={facility.uuid}>
                        {facility.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex min-w-[200px] flex-1 flex-col gap-2">
                <label htmlFor="user" className="text-xs font-medium text-[var(--gray-primary)]">
                  Usuário que fez a transferência
                </label>
                <Select
                  value={userUuid || 'all'}
                  onValueChange={(value) => {
                    setUserUuid(value === 'all' ? undefined : value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="user" className="h-10 w-full">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {usersData?.users?.map((user) => (
                      <SelectItem key={user.uuid} value={user.uuid}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="mx-auto w-[80%] rounded-[10px]">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-neutral-600">
              <TableHead className="w-[15%] border-r border-neutral-600 last:border-r-0">Data/Hora</TableHead>
              <TableHead className="w-[15%] border-r border-neutral-600 last:border-r-0">Usuário</TableHead>
              <TableHead className="w-[20%] border-r border-neutral-600 last:border-r-0">
                Estabelecimento Anterior
              </TableHead>
              <TableHead className="w-[20%] border-r border-neutral-600 last:border-r-0">
                Novo Estabelecimento
              </TableHead>
              <TableHead className="w-[30%] border-r border-neutral-600 last:border-r-0">Comentários</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                    <span className="text-muted-foreground text-sm">Carregando...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : !data?.logs.length ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  <span className="text-muted-foreground text-sm">
                    {hasActiveFilters
                      ? 'Nenhuma transferência encontrada com os filtros aplicados'
                      : 'Nenhuma transferência registrada'}
                  </span>
                </TableCell>
              </TableRow>
            ) : (
              data.logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="border-r border-neutral-600 font-medium last:border-r-0">
                    {formatDateTime(log.datetime)}
                  </TableCell>
                  <TableCell className="border-r border-neutral-600 last:border-r-0">
                    <div className="flex flex-col">
                      <span className="font-medium">{log.user.name}</span>
                      {log.user.email && <span className="text-xs text-gray-500">{log.user.email}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="border-r border-neutral-600 last:border-r-0">
                    {log.previousFacility.name}
                  </TableCell>
                  <TableCell className="border-r border-neutral-600 last:border-r-0">{log.newFacility.name}</TableCell>
                  <TableCell className="border-r border-neutral-600 last:border-r-0">
                    {log.comments ? (
                      <span className="text-sm">{log.comments}</span>
                    ) : (
                      <span className="text-muted-foreground text-sm italic">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex flex-col items-center gap-4">
          <TablePagination page={page} totalItems={data.total} itemsPerPage={pageSize} onPageChange={setPage} />
          <p className="text-muted-foreground text-sm">
            Mostrando {(page - 1) * pageSize + 1} a {Math.min(page * pageSize, data.total)} de {data.total} registros
          </p>
        </div>
      )}
    </div>
  );
}
